import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Loader2 } from 'lucide-react';
import { useProAuth } from '@/lib/auth-context';
import { AuthLayout } from '@/components/layout/AuthLayout';

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useProAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      const from = (location.state as { from?: string } | null)?.from ?? '/tableau-de-bord';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <h2 className="text-xl font-semibold text-neutral-900 mb-1">
        {t('pro.auth.login_title', 'Connexion')}
      </h2>
      <p className="text-sm text-neutral-500 mb-6">
        {t('pro.auth.login_subtitle', 'Accédez à votre espace pro')}
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-neutral-700 mb-1.5 block">
            {t('pro.auth.email', 'Email')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="vous@exemple.com"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium text-neutral-700">
              {t('pro.auth.password', 'Mot de passe')}
            </label>
            <Link to="/forgot-password" className="text-xs text-orange-600 hover:underline">
              {t('pro.auth.forgot', 'Mot de passe oublié ?')}
            </Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="••••••••"
            />
          </div>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60 inline-flex items-center justify-center gap-2"
          style={{ background: 'var(--pro-primary)' }}
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('pro.auth.login_button', 'Se connecter')}
        </button>
      </form>

      <p className="text-sm text-neutral-500 text-center mt-6">
        {t('pro.auth.no_account', 'Pas encore de compte ?')}{' '}
        <Link to="/register" className="text-orange-600 font-medium hover:underline">
          {t('pro.auth.create_account', 'Créer un compte')}
        </Link>
      </p>
    </AuthLayout>
  );
}
